import { Fragment, useEffect, useRef, type MouseEvent, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import { Checkbox } from '../../platform/primitives/Checkbox'
import { reconcileGroups, type Group, type Selection } from '../../platform/primitives/DataGrid'
import { MerchantAvatar } from '../../platform/primitives/MerchantAvatar'
import { Money } from '../../platform/primitives/Money'
import type { Account } from '../accounts/useAccounts'
import type { CategoryListStatus } from '../categories/setCategoryLabel'
import { CategoryPill } from './CategoryPill'
import { MerchantPill } from './MerchantPill'
import type { Txn } from './transactionWire'
import { accountLabel, avatarOf, rowSummary, shortDate } from './txnDisplay'
import './transactions.css'

type Props = {
  groups: Group<Txn>[]
  accounts: ReadonlyMap<string, Pick<Account, 'name' | 'mask'>>
  categories: ReadonlyMap<string, string>
  /** The categories list's state, so a pill names a set id it cannot name yet as "Loading", not "Unknown" (R7). */
  categoriesStatus: CategoryListStatus
  /** Select mode shows a checkbox per card. It needs `selection` as well; one without the other is a plain list. */
  selecting?: boolean
  selection?: Selection<Txn>
  /** Outside select mode, holding a card for `LONG_PRESS_MS` calls this with its row. */
  onLongPress?: (row: Txn) => void
  /** What a card link tells the detail page to go back to: a `?search` or a `/cashflow/…` URL. Defaults to this page's own search. */
  backFrom?: string
  /** Rows only: the day bands are drawn WITHOUT their subtotals (stripped here, whatever `groups` carries) and nothing reconciles. Default off. */
  plain?: boolean
  /**
   * Given (outside select mode), the category on line two is a button that opens the picker. A
   * button cannot sit inside a link, so the card stops being one: the merchant name becomes the
   * link and stretches over the card, and the pill sits above it. Absent, the card is as before.
   */
  onCategoryClick?: (t: Txn, opener: HTMLButtonElement) => void
  /** Given (outside select mode), a card with no merchant also carries "Needs merchant" on line two, after the category, which opens the merchant picker; the card stretches as for `onCategoryClick`. */
  onMerchantClick?: (t: Txn, opener: HTMLButtonElement) => void
}

/** How long a card must be held to enter select mode. */
export const LONG_PRESS_MS = 500

/**
 * The phone list (M6-R24): a `<ul>` of cards per day, not a table. A table of
 * six columns does not fit 375px, and squeezing it is how the amount ends up
 * off screen.
 *
 * Outside select mode the **whole card is one link** and nothing inside it is
 * interactive, so a thumb anywhere on it opens the transaction and a screen
 * reader hears one stop per row. Its name is merchant, date and amount: the
 * visible line-two detail is not what identifies a row.
 *
 * The same `reconcileGroups` check the desktop grid runs is run here, so the
 * two renderings of one page cannot disagree about a day's total.
 */
export function TransactionCards({ groups, accounts, categories, categoriesStatus, selecting, selection, onLongPress, backFrom, plain = false, onCategoryClick, onMerchantClick }: Props) {
  const shown: Group<Txn>[] = plain ? groups.map(({ label, rows }) => ({ label, rows })) : reconcileGroups(groups, (t) => t.amountCents, (t) => t.currency)
  const select = selecting === true && selection !== undefined ? selection : undefined

  return (
    <div className="txn-cards">
      {shown.map((g, i) => (
        <section key={`${i}:${g.label}`} className="txn-cards__day">
          <h3 className="txn-cards__band">
            <span className="txn-cards__label">{g.label}</span>
            {/* Only when there is money to show: an EMPTY span in this sticky flex band crashed chrome-headless-shell's
                renderer (the overflow sweep on /budget/:id, where `plain` bands carry none). */}
            {g.subtotals !== undefined && g.subtotals.length > 0 && (
              <span className="txn-cards__subtotals">
                {g.subtotals.map((t, n) => (
                  <Fragment key={t.currency}>
                    {n > 0 && <span aria-hidden="true"> · </span>}
                    <span data-currency={t.currency}>
                      <Money cents={t.cents} currency={t.currency} kind="flow" />
                    </span>
                  </Fragment>
                ))}
              </span>
            )}
          </h3>
          <ul className="txn-cards__list">
            {g.rows.map((t) => (
              <li key={t.id} className="txn-cards__item">
                <Card
                  t={t}
                  accounts={accounts}
                  categories={categories}
                  categoriesStatus={categoriesStatus}
                  selection={select}
                  onLongPress={onLongPress}
                  backFrom={backFrom}
                  onCategoryClick={onCategoryClick}
                  onMerchantClick={onMerchantClick}
                />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function Card({
  t,
  accounts,
  categories,
  categoriesStatus,
  selection,
  onLongPress,
  backFrom,
  onCategoryClick,
  onMerchantClick,
}: {
  t: Txn
  accounts: Props['accounts']
  categories: Props['categories']
  categoriesStatus: Props['categoriesStatus']
  selection: Selection<Txn> | undefined
  onLongPress: Props['onLongPress']
  backFrom: Props['backFrom']
  onCategoryClick: Props['onCategoryClick']
  onMerchantClick: Props['onMerchantClick']
}) {
  const { search } = useLocation()
  const from = backFrom ?? search
  const hold = useRef<number | undefined>(undefined)
  /** Set when a press became a long-press, so the click that follows is swallowed. */
  const fired = useRef(false)
  const cancel = () => {
    window.clearTimeout(hold.current)
    hold.current = undefined
  }
  useEffect(() => cancel, [])
  const account = accounts.get(t.accountId)
  const category = t.categoryId === null ? undefined : categories.get(t.categoryId)
  const meta = (pill: ReactNode) => (
    <span className="txn-card__line txn-card__line--meta">
      {pill}
      {/* After the category, on the same line, so the two wrap together on a narrow card. */}
      {selection === undefined && onMerchantClick !== undefined && <MerchantPill t={t} onMerchantClick={onMerchantClick} />}
      {t.isPending && <span className="txn-chip">Pending</span>}
      <span>{account ? accountLabel(account) : '—'}</span>
      <span>{shortDate(t.date)}</span>
    </span>
  )
  const amount = (
    <span className="txn-amount">
      <Money cents={t.amountCents} currency={t.currency} kind="flow" />
    </span>
  )
  const body = (
    <>
      <MerchantAvatar merchant={avatarOf(t)} />
      <span className="txn-card__text">
        <span className="txn-card__line">
          <span className="txn-card__merchant">{t.merchant}</span>
          {amount}
        </span>
        {meta(
          t.categoryId === null ? (
            <span className="txn-chip txn-chip--warn">Needs category</span>
          ) : (
            category !== undefined && <span>{category}</span>
          ),
        )}
      </span>
    </>
  )

  const cls = `txn-card${t.categoryId === null ? ' txn-card--uncategorised' : ''}`

  if (selection) {
    // Select mode: the checkbox is the control, so the card cannot itself be a
    // link (an interactive nested in one), and the category stays plain text
    // even when `onCategoryClick` is given. Opening a transaction is what leaving
    // select mode is for.
    return (
      <div
        className={`${cls} txn-card--selecting${selection.isSelected(t) ? ' txn-card--selected' : ''}`}
        onClick={(e) => {
          // The box toggles itself; a tap anywhere else on the card is the same choice.
          if (!(e.target instanceof Element && e.target.closest('.checkbox'))) selection.onToggle(t)
        }}
      >
        <Checkbox checked={selection.isSelected(t)} onChange={() => selection.onToggle(t)} label={selection.rowLabel(t)} />
        {body}
      </div>
    )
  }

  const press = {
    onPointerDown: () => {
      if (onLongPress === undefined) return
      fired.current = false
      cancel()
      hold.current = window.setTimeout(() => {
        hold.current = undefined
        fired.current = true
        onLongPress(t)
      }, LONG_PRESS_MS)
    },
    onPointerMove: cancel,
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onContextMenu: (e: MouseEvent) => {
      // Touch browsers raise a context menu on a long hold; it would sit on top of select mode.
      if (onLongPress !== undefined) e.preventDefault()
    },
  }

  /** The click a long-press leaves behind is not a tap: swallowed, so it opens nothing. */
  const swallow = (e: MouseEvent) => {
    if (!fired.current) return false
    fired.current = false
    e.preventDefault()
    return true
  }

  if (onCategoryClick !== undefined || onMerchantClick !== undefined) {
    // The stretched card (the `.cf-rank__link` pattern): the merchant name is the link and its
    // ::after covers the card, so a tap anywhere still opens the transaction; the pill sits above.
    return (
      <div
        className={`${cls} txn-card--stretch hoverable`}
        {...press}
        // Capture, so the swallowed click reaches neither the link nor the pill.
        onClickCapture={(e) => {
          if (swallow(e)) e.stopPropagation()
        }}
      >
        <MerchantAvatar merchant={avatarOf(t)} />
        <span className="txn-card__text">
          <span className="txn-card__line">
            <Link
              to={`/transactions/${t.id}`}
              state={{ from }}
              className="txn-card__merchant txn-card__stretch-link"
              aria-label={rowSummary(t)}
            >
              {t.merchant}
            </Link>
            {amount}
          </span>
          {meta(
            onCategoryClick !== undefined ? (
              <CategoryPill t={t} name={category} status={categoriesStatus} onCategoryClick={onCategoryClick} />
            ) : t.categoryId === null ? (
              <span className="txn-chip txn-chip--warn">Needs category</span>
            ) : (
              category !== undefined && <span>{category}</span>
            ),
          )}
        </span>
      </div>
    )
  }

  return (
    <Link to={`/transactions/${t.id}`} state={{ from }} className={`${cls} hoverable`} aria-label={rowSummary(t)} {...press} onClick={swallow}>
      {body}
    </Link>
  )
}
